import React, { useState } from 'react';
import { Box, Stepper, Step, StepLabel, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Modal from 'components/Modal';

import { StepType } from '../types';
import { useEditionFormContext } from './EditionFormWrapper';
import CloseConfirmationModal from '../Modals/CloseConfirmationModal';
import StepperIcon from './StepperIcon';

interface IEditionModalProps {
  steps: Array<StepType>;
  isLoading: boolean;
}

const EditionModal: React.FC<IEditionModalProps> = ({ steps }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeStep, isDirty, updateId } = useEditionFormContext();
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  const handleClose = () => {
    if (isDirty) {
      setShowCloseConfirmation(true);
    } else {
      navigate({ pathname: '/documents/center' });
    }
  };

  const handleCancelClose = () => {
    setShowCloseConfirmation(false);
  };

  if (showCloseConfirmation) {
    return <CloseConfirmationModal onCancelClick={handleCancelClose} />;
  }

  return (
    <Modal
      minWidth='500px'
      maxWidth='600px'
      minHeight='600px'
      icon={<i className={`fa-regular fa-${updateId ? 'pen' : 'plus'}`} />}
      title={t('documents.edition.modal.center_title')}
      onClose={handleClose}
      centerTitle
    >
      <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
        <Box sx={{ width: '45%', margin: '0 auto' }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            connector={<Box sx={{ height: '1px', backgroundColor: 'border.primary', width: '45%', position: 'absolute', top: '13px', left: 'calc(-50% + 30px)', right: 'calc(50% + 20px)' }} />}
          >
            {steps.map(({ label, icon }, index) => {
              const stepProps: { completed?: boolean } = {};
              return (
                <Step key={index} {...stepProps}>
                  <StepLabel
                    StepIconComponent={StepperIcon}
                    StepIconProps={{ icon, active: activeStep === index }}
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'center',
                      '& .MuiStepLabel-iconContainer': {
                        paddingRight: 0,
                        paddingBottom: 1,
                      },
                      '& .MuiStepLabel-labelContainer .MuiStepLabel-alternativeLabel': {
                        marginTop: 0,
                      }
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        marginTop: '0px',
                        color: activeStep === index ? 'primary.main' : 'text.secondary',
                        fontWeight: activeStep === index ? '500' : '400',
                      }}
                    >
                      {label}
                    </Typography>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
        <Box sx={{ mt: 2 }}>{steps[activeStep].component}</Box>
      </Box>
    </Modal>
  );
};

export default EditionModal;